export const CLOUDINARY_CLOUD_NAME =
	process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'drey3dgn9'

export const CLOUDINARY_UPLOAD_PRESET =
	process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'mydealteams'

export type UploadSource = 'drag-and-drop' | 'browse' | 'external' | string | null

export interface NormalizedFile {
	uuid: string | null
	name: string | null
	size: number | null
	isStored: boolean | null
	isImage: boolean | null
	mimeType: string | null
	cdnUrl: string | null
	originalUrl: string | null
	source: UploadSource
	rawFileInfo: unknown
}

export interface CloudinaryUploadResult {
	public_id: string
	secure_url: string
	url: string
	original_filename: string
	bytes: number
	format: string
	resource_type: 'image' | 'video' | 'raw'
	created_at: string
	width?: number
	height?: number
	[key: string]: unknown
}

/**
 * Upload a file or URL to Cloudinary using unsigned upload preset.
 * Works client-side — no backend proxy needed.
 */
export async function uploadToCloudinary(
	file: File | string,
	options?: { folder?: string; resourceType?: 'image' | 'video' | 'raw' | 'auto' },
): Promise<CloudinaryUploadResult> {
	const formData = new FormData()
	if (typeof file === 'string') {
		formData.append('file', file)
	} else {
		formData.append('file', file)
	}
	formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET)
	if (options?.folder) formData.append('folder', options.folder)

	const resourceType = options?.resourceType || 'auto'
	const res = await fetch(
		`https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/${resourceType}/upload`,
		{ method: 'POST', body: formData },
	)
	if (!res.ok) {
		const errorBody = await res.text().catch(() => res.statusText)
		throw new Error(`Cloudinary upload failed: ${errorBody}`)
	}
	return res.json()
}

/**
 * Convert a Cloudinary upload result to the normalized file format
 * used throughout the app.
 */
export function normalizeCloudinaryResult(
	result: CloudinaryUploadResult,
	source: UploadSource = 'drag-and-drop',
): NormalizedFile {
	return {
		uuid: result.public_id ?? null,
		name: result.original_filename ?? null,
		size: result.bytes ?? null,
		isStored: true,
		isImage: result.resource_type === 'image',
		mimeType: result.format ? `${result.resource_type}/${result.format}` : null,
		cdnUrl: result.secure_url ?? null,
		originalUrl: result.secure_url ?? null,
		source,
		rawFileInfo: result,
	}
}

/**
 * Cloudinary transform presets matching the old UploadCare transforms.
 */
export const TRANSFORM = {
	/** 600×480 thumbnail with auto quality and format */
	THUMB: 'w_600,h_480,c_fit,q_auto,f_auto',
	/** 800×600 preview with auto quality and format */
	PREVIEW: 'w_800,h_600,c_fit,q_auto,f_auto',
	/** Auto-width preview with quality optimization */
	PREVIEW_AUTO: 'w_800,c_limit,q_auto,f_auto',
} as const

/**
 * Apply image transformations to a CDN URL.
 * Handles Cloudinary URLs (insert transforms into path) and
 * legacy UploadCare URLs (append transforms) during transition.
 */
export const transformUrl = (url: string, transforms: string): string => {
	if (!url) return url
	// Cloudinary: insert transforms between /upload/ and the public_id
	if (url.includes('res.cloudinary.com')) {
		return url.replace('/upload/', `/upload/${transforms}/`)
	}
	// Legacy UploadCare URLs still in the database
	if (url.includes('ucarecdn.com')) return url
	return url
}

const sanitizeAttachmentName = (name: string): string =>
	encodeURIComponent(name).replace(/[^a-zA-Z0-9._%-]/g, '_')

/**
 * Build a Cloudinary delivery URL that forces an `attachment` response
 * with the original filename, instead of Cloudinary's default
 * public-id-based filename. Cross-origin downloads ignore the HTML5
 * `download` attribute, so this flag is required for the user-supplied
 * filename to land on disk.
 *
 * Use only for download anchors — applying it to preview URLs would
 * force the browser to download instead of render inline.
 */
export const cloudinaryAttachmentUrl = (
	url: string | null | undefined,
	filename: string | null | undefined,
): string => {
	if (!url) return ''
	if (!url.includes('res.cloudinary.com')) return url
	if (!filename) return url
	if (url.includes('/fl_attachment')) return url
	return url.replace(
		'/upload/',
		`/upload/fl_attachment:${sanitizeAttachmentName(filename)}/`,
	)
}

export const getExtensionFromName = (name?: string | null) => {
	if (!name) return ''
	const parts = name.split('.')
	return parts.length > 1 ? parts.pop()?.toLowerCase() ?? '' : ''
}

// ── Backward-compatible aliases ──
// These allow existing code that hasn't been updated yet to keep working.
// Remove these once all imports are migrated.
/** @deprecated Use UploadSource */
export type UploadcareSource = UploadSource
/** @deprecated Use NormalizedFile */
export type NormalizedUploadcareFile = NormalizedFile
/** @deprecated Use CLOUDINARY_CLOUD_NAME */
export const UPLOADCARE_PUBLIC_KEY = CLOUDINARY_CLOUD_NAME
/** @deprecated Use transformUrl */
export const ucTransform = transformUrl
